import { FC, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { InfoCard, InfoTitle, NumberText, PreviousPeriodComparison } from '@builder';
import { InfoPanelProps } from './InfoPanel.types';
import {
  StyledContent,
  StyledDescription,
  StyledDivisor,
  StyledInfoPanel,
  StyledRemaining,
  StyledConversionRateGoal,
} from './InfoPanel.styles';
import { Sizes, Weights } from '@/common/types';
import { Box } from '@base-app';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { formatNumber } from '@/modules/study-insights/utils';

const InfoPanel: FC<InfoPanelProps> = ({
  containerStyles,
  description,
  infoTitle,
  numberText,
  previousPeriod,
  remaining,
  conversionRateGoal,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const ref = useRef<HTMLDivElement>(null);
  const isNumber = typeof numberText === 'number';

  return (
    <StyledInfoPanel css={{ ...containerStyles }} ref={ref}>
      <InfoCard css={{ width: '260px', overflow: 'hidden' }} hasBorder>
        <StyledContent>
          <InfoTitle>{infoTitle}</InfoTitle>
          <NumberText css={{ lineHeight: '$10' }} size={isNumber ? Sizes.LARGE : Sizes.MEDIUM} weight={Weights.BOLD}>
            {numberText}
          </NumberText>
          {description && <StyledDescription>{description}</StyledDescription>}
          <Box css={{ marginTop: '$4', width: '100px' }}>
            {previousPeriod.period && previousPeriod.percentage !== 0 && (
              <PreviousPeriodComparison
                percentage={previousPeriod.percentage}
                period={previousPeriod.period}
                testId="info-panel-period"
              />
            )}
          </Box>

          {!!conversionRateGoal && conversionRateGoal > 0 && (
            <>
              <StyledDivisor />
              <StyledRemaining>
                <Trans
                  t={t}
                  i18nKey="participant_recruitment.conversion_rate_goal"
                  values={{ conversion_rate_goal_value: formatNumber(conversionRateGoal) }}
                />
              </StyledRemaining>
            </>
          )}
          {!!remaining && remaining > 0 && (
            <>
              <StyledDivisor />
              <StyledConversionRateGoal>
                <Trans
                  t={t}
                  i18nKey="participant_recruitment.remaining"
                  values={{ remaining: formatNumber(remaining) }}
                />
              </StyledConversionRateGoal>
            </>
          )}
        </StyledContent>
      </InfoCard>
    </StyledInfoPanel>
  );
};

export default InfoPanel;
